// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { ChevronRight, Building2 } from 'lucide-react';
import { PageHeader, Button, InfoBanner } from '../../components/WorkflowFormPrimitives';
import { EditableClosingDateChip } from '../../../shared/EditableClosingDateChip';
import { useCompanyContext } from '../../context/CompanyContext';
import { useSaveClientClosingDate } from '../../hooks/useSaveClientClosingDate';
import { useAuth } from '../../../../contexts/AuthContext';
import { getClientWorkspaceCompany } from '../../../../services/AnalysisApiClient';

const readonlyBoxClass = 'w-full min-h-9 px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-md text-gray-800';

export const TransactionInfo = ({ formData, setFormData, setActiveTab, continueMode }) => {
  const { currentAnalysis, isCurrentAnalysisLocked, archivedAnalyses } = useCompanyContext();
  const { saveClosingDate, canEditClosing } = useSaveClientClosingDate(archivedAnalyses);
  const { user } = useAuth();
  const companyLinked = !!(currentAnalysis?.companyId && currentAnalysis?.lawFirmId);
  const [workspaceCompany, setWorkspaceCompany] = useState(null);

  useEffect(() => {
    if (user?.loginType !== 'Client') {
      setWorkspaceCompany(null);
      return;
    }
    let cancelled = false;
    getClientWorkspaceCompany()
      .then((row) => {
        if (!cancelled) setWorkspaceCompany(row);
      })
      .catch(() => {
        if (!cancelled) setWorkspaceCompany(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.loginType]);

  /** Sync client workspace company into form state when this analysis is not firm-linked (avoid overriding enrichment). */
  useEffect(() => {
    if (!workspaceCompany || companyLinked) return;
    const wn = String(workspaceCompany.companyName || '').trim();
    const wc = String(workspaceCompany.closingDate || '').trim();
    setFormData((p) => {
      let next = { ...p };
      let changed = false;
      if (wn && p.companyName !== wn) {
        next.companyName = wn;
        changed = true;
      }
      if (wc && p.closingDate !== wc) {
        next.closingDate = wc;
        changed = true;
      }
      return changed ? next : p;
    });
  }, [workspaceCompany, companyLinked]);

  useEffect(() => {
    if (user?.loginType !== 'Client') return;
    const un = String(user?.companyName || '').trim();
    if (!un) return;
    setFormData((p) => {
      if (String(p.companyName || '').trim()) return p;
      return { ...p, companyName: un };
    });
  }, [user?.loginType, user?.companyName]);

  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));
  const dealPriceRaw = formData.dealPrice || '';
  const afrRaw = formData.afr || '';

  const handleDealPriceChange = (e) => {
    const val = e.target.value || '';
    const cleaned = val.replace(/[^0-9.]/g, '');
    set('dealPrice', cleaned);
  };

  const handleAfrChange = (e) => {
    const val = e.target.value || '';
    const cleaned = val.replace(/[^0-9.]/g, '');
    set('afr', cleaned);
  };

  const nameFromAnalysis = String(formData.companyName || '').trim();
  const closeFromAnalysis = String(formData.closingDate || '').trim();
  const nameFromWorkspace = String(workspaceCompany?.companyName || '').trim();
  const closeFromWorkspace = String(workspaceCompany?.closingDate || '').trim();
  const nameFromLogin = String(user?.companyName || '').trim();

  const companyNameDisplay = String(
    companyLinked ? nameFromAnalysis || nameFromWorkspace : nameFromWorkspace || nameFromAnalysis || nameFromLogin
  ).trim();
  const closingDateRaw = String(
    companyLinked ? closeFromAnalysis || closeFromWorkspace : closeFromWorkspace || closeFromAnalysis
  ).trim();
  const hasCompanyDisplay = companyNameDisplay.length > 0;
  const hasClosingDisplay = String(formData.closingDate || closingDateRaw).trim().length > 0;
  const canContinue = hasCompanyDisplay && hasClosingDisplay;
  const hasAuthoritativeSource = companyLinked || !!workspaceCompany;
  const closingEditable = canEditClosing && !isCurrentAnalysisLocked;
  const forwardPrimaryLabel = isCurrentAnalysisLocked ? 'Continue' : 'Save and continue';

  const handleClosingDateChange = async (isoYmd) => {
    const next = String(isoYmd ?? '').trim();
    if (!next) return;
    set('closingDate', next);
    await saveClosingDate(next);
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Transaction Details" subtitle="Enter Basic Transaction Details for the 280G Analysis" />
      {companyLinked && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200/80 bg-emerald-50/80 px-4 py-3 text-xs text-emerald-900">
          <Building2 className="h-4 w-4 shrink-0 mt-0.5 text-emerald-700" aria-hidden />
          <p>
            <span className="font-medium">Company name</span> comes from your firm company record. Use the edit control
            next to <span className="font-medium">Closing date</span> below to update the date for this analysis, or
            change it in the firm portal (Companies).
          </p>
        </div>
      )}
      {!companyLinked && workspaceCompany && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200/80 bg-emerald-50/80 px-4 py-3 text-xs text-emerald-900">
          <Building2 className="h-4 w-4 shrink-0 mt-0.5 text-emerald-700" aria-hidden />
          <p>
            <span className="font-medium">Company name</span> comes from your company profile. Use the edit control next
            to <span className="font-medium">Closing date</span> below; changes save to your company record.
          </p>
        </div>
      )}
      {!hasAuthoritativeSource && (
        <InfoBanner showIcon>
          Company name loads from your company profile or a firm-linked analysis. Set the closing date below once your
          account is tied to a company or this analysis is saved.
        </InfoBanner>
      )}
      {/* Form fields live inside WorkflowStepBody’s outer Card — avoid a nested Card for clearer hierarchy. */}
      <div className="grid w-full grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2 md:gap-y-8">
        <div className="min-w-0">
          <label className="block text-xs font-medium text-gray-700 mb-2">Company Name</label>
          <div className={readonlyBoxClass} title={companyNameDisplay || undefined}>
            {companyNameDisplay || '—'}
          </div>
        </div>
        <div className="min-w-0">
          <EditableClosingDateChip
            variant="field"
            closingDate={formData.closingDate || closingDateRaw}
            onSave={handleClosingDateChange}
            disabled={!closingEditable}
          />
        </div>
        <div className="min-w-0">
          <label className="block text-xs font-medium text-gray-700 mb-2">Deal Price per Share</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={dealPriceRaw}
              onChange={handleDealPriceChange}
              placeholder="0.00"
              className="w-full h-9 pl-7 pr-3 text-xs bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all placeholder:text-gray-400"
            />
          </div>
        </div>
        <div className="min-w-0">
          <label className="block text-xs font-medium text-gray-700 mb-2">Applicable Federal Rate (AFR)</label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={afrRaw}
              onChange={handleAfrChange}
              placeholder="5.00"
              className="w-full h-9 pl-3 pr-8 text-xs bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all placeholder:text-gray-400"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">%</span>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Short-term AFR as a percentage. 120% of this rate is used for present value calculations per Q&amp;A-32.
          </p>
        </div>
      </div>
      <InfoBanner showIcon>
        <strong>Present Value Calculation:</strong> Per Q&A-31 and Q&A-32, payments made after the change in control
        must be discounted to present value using 120% of the applicable federal rate (AFR), compounded semiannually.
        The current short-term AFR can be found on the{' '}
        <a
          href="https://www.irs.gov/applicable-federal-rates"
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-700 hover:text-emerald-900 underline"
        >
          IRS website
        </a>
        .
      </InfoBanner>
      <div className="flex justify-end gap-2">
        {continueMode === 'dual' ? (
          <>
            <Button
              variant="secondary"
              disabled={!canContinue}
              onClick={() => setActiveTab('individuals', { persist: false })}
            >
              Continue <ChevronRight size={14} className="ml-1.5" />
            </Button>
          </>
        ) : (
          <Button disabled={!canContinue} onClick={() => setActiveTab('individuals', { persist: true })}>
            {forwardPrimaryLabel} <ChevronRight size={14} className="ml-1.5" />
          </Button>
        )}
      </div>
    </div>
  );
};
